// Tone curve editor (§6.4): master RGB then per channel. Click adds a point, drag moves it (end points
// move vertically only), double-click removes an inner point. Live while dragging, one undo step on release.
import { useMemo, useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { useActive } from './useActive';
import { useT, type MessageKey } from '../../i18n';
import { monotoneCubic, normalizePoints, IDENTITY_CURVE, type Pt } from '../../color/curve';
import type { Settings } from '../../model/types';

type Ch = keyof Settings['curve'];
const CHANNELS: { key: Ch; label: MessageKey; color: string }[] = [
  { key: 'master', label: 'curveMaster', color: 'currentColor' },
  { key: 'r', label: 'curveR', color: 'var(--curve-r)' },
  { key: 'g', label: 'curveG', color: 'var(--curve-g)' },
  { key: 'b', label: 'curveB', color: 'var(--curve-b)' },
];
const SIZE = 200;
const HIT = 0.04;

export function CurvePanel() {
  const t = useT();
  const { settings, update } = useActive();
  const [ch, setCh] = useState<Ch>('master');
  const svg = useRef<SVGSVGElement>(null);
  const drag = useRef<{ index: number; pts: Pt[] } | null>(null);
  const pts = settings.curve[ch];
  const color = CHANNELS.find((c) => c.key === ch)!.color;

  const path = useMemo(() => {
    const f = monotoneCubic(pts);
    const d: string[] = [];
    for (let i = 0; i <= 64; i++) {
      const x = i / 64;
      d.push(`${i ? 'L' : 'M'}${(x * SIZE).toFixed(1)},${((1 - f(x)) * SIZE).toFixed(1)}`);
    }
    return d.join('');
  }, [pts]);

  const toCurve = (e: PointerEvent): Pt => {
    const r = svg.current!.getBoundingClientRect();
    const c = (v: number) => Math.min(1, Math.max(0, v));
    return { x: c((e.clientX - r.left) / r.width), y: c(1 - (e.clientY - r.top) / r.height) };
  };
  const nearest = (p: Pt, list: Pt[]) => list.findIndex((q) => Math.hypot(q.x - p.x, q.y - p.y) < HIT * 1.5);

  const set = (next: Pt[], final: boolean) => update((s) => void (s.curve[ch] = next), final);

  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    const p = toCurve(e);
    let list = normalizePoints(pts);
    let index = nearest(p, list);
    if (index < 0) {
      list = normalizePoints([...list, p]);
      index = list.findIndex((q) => q.x === p.x);
      set(list, false);
    }
    drag.current = { index, pts: list };
    svg.current!.setPointerCapture(e.pointerId);
  };
  const move = (e: PointerEvent<SVGSVGElement>, final: boolean) => {
    const d = drag.current;
    if (!d) return;
    const p = toCurve(e);
    const list = d.pts.map((q) => ({ ...q }));
    const i = d.index;
    const last = list.length - 1;
    // End points keep their x; inner points stay strictly between their neighbours.
    const x =
      i === 0 ? 0 : i === last ? 1 : Math.min(list[i + 1]!.x - 0.01, Math.max(list[i - 1]!.x + 0.01, p.x));
    list[i] = { x, y: p.y };
    d.pts = list;
    set(list, final);
    if (final) drag.current = null;
  };
  const onDouble = (e: MouseEvent<SVGSVGElement>) => {
    const r = svg.current!.getBoundingClientRect();
    const p = { x: (e.clientX - r.left) / r.width, y: 1 - (e.clientY - r.top) / r.height };
    const list = normalizePoints(pts);
    const i = nearest(p, list);
    if (i > 0 && i < list.length - 1)
      set(
        list.filter((_, k) => k !== i),
        true,
      );
  };

  return (
    <div>
      <div role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {CHANNELS.map((c) => (
          <button
            key={c.key}
            role="tab"
            aria-selected={ch === c.key}
            data-testid={`curve-tab-${c.key}`}
            onClick={() => setCh(c.key)}
            style={ch === c.key ? { background: 'var(--accent-fill)', color: 'var(--on-accent)' } : undefined}
          >
            {t(c.label)}
          </button>
        ))}
      </div>
      <svg
        ref={svg}
        data-testid="curve-editor"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width="100%"
        role="img"
        aria-label={t('curveHint')}
        style={{
          aspectRatio: '1',
          touchAction: 'none',
          border: '1px solid var(--line)',
          cursor: 'crosshair',
        }}
        onPointerDown={onDown}
        onPointerMove={(e) => move(e, false)}
        onPointerUp={(e) => move(e, true)}
        onDoubleClick={onDouble}
      >
        {[0.25, 0.5, 0.75].map((g) => (
          <g key={g} stroke="currentColor" strokeOpacity={0.15}>
            <line x1={g * SIZE} y1={0} x2={g * SIZE} y2={SIZE} />
            <line x1={0} y1={g * SIZE} x2={SIZE} y2={g * SIZE} />
          </g>
        ))}
        <line
          x1={0}
          y1={SIZE}
          x2={SIZE}
          y2={0}
          stroke="currentColor"
          strokeOpacity={0.25}
          strokeDasharray="4 4"
        />
        <path d={path} fill="none" stroke={color} strokeWidth={2} />
        {normalizePoints(pts).map((p, i) => (
          <circle key={i} cx={p.x * SIZE} cy={(1 - p.y) * SIZE} r={4} fill={color} stroke="var(--pure-white)" />
        ))}
      </svg>
      <div style={{ fontSize: 12, margin: '4px 0', opacity: 0.8 }}>{t('curveHint')}</div>
      <button
        data-testid="curve-reset"
        onClick={() =>
          set(
            IDENTITY_CURVE.map((p) => ({ ...p })),
            true,
          )
        }
      >
        {t('curveReset')}
      </button>
    </div>
  );
}
